import type { Region } from "./types";

/**
 * THE REGION CORRIDORS — geography ported 1:1 from the proven engine, every
 * description re-derived for what each corridor does to an aerial shoot.
 *
 * These paragraphs do double duty. They are the region hub pages' selling copy,
 * and `airspaceSummary` is ladder level 2 (Operating Brief §6.4): the verified
 * corridor-level airspace claim a location page falls back to when a
 * site-specific record has not yet been confirmed against NAV Drone.
 *
 * The corridor claim is always narrower than a site claim. It describes the
 * airspace the corridor sits under and states that the specific site is checked
 * before every flight. That is true, useful, and citable.
 */
export const REGIONS: Region[] = [
  {
    slug: "calgary-centre",
    name: "Calgary City Centre",
    shortName: "Calgary Centre",
    description:
      "The inner city — Beltline, Eau Claire, East Village, Kensington, Mission and the commercial core. Tower shadow moves fast here: between 10am and 2pm the downtown grid throws hard geometry across every low-rise roof, and a listing shot at the wrong hour loses its facade to a neighbouring building. Inner-city work is scheduled around sun azimuth, not around convenience.",
    airspaceSummary:
      "Downtown Calgary sits inside the Calgary International (CYYC) Class C control zone, and the Foothills and Peter Lougheed hospital heliports add their own approach considerations. Every inner-city flight is an authorised flight, filed before the shoot is confirmed.",
    adjacentRegions: ["calgary-nw", "calgary-ne", "calgary-sw", "calgary-se"],
  },
  {
    slug: "calgary-nw",
    name: "Northwest Calgary",
    shortName: "Calgary NW",
    description:
      "Calgary's northwest — Tuscany, Arbour Lake, Royal Oak, Sage Hill, Evanston, Nolan Hill, Valley Ridge and the newest phases beyond. The quadrant where the city meets the Bow River escarpment, so elevation change is real: a home on the west edge of Valley Ridge sits a hundred metres above the river it looks at. That drop is the shot.",
    airspaceSummary:
      "The northwest sits between the CYYC control zone to the east and Springbank (CYBW) to the west, so the corridor is largely controlled airspace with a narrow uncontrolled seam. Authorisation is filed per site rather than assumed.",
    adjacentRegions: ["calgary-centre", "calgary-ne", "rocky-view", "springbank-elbow"],
  },
  {
    slug: "calgary-ne",
    name: "Northeast Calgary",
    shortName: "Calgary NE",
    description:
      "Calgary's northeast — Saddle Ridge, Skyview, Redstone, Cornerstone, Martindale, Taradale and the airport corridor. The most airspace-constrained quadrant in the city and, for that reason, the one most competitors quietly decline. Flat ground, wide lots, big industrial parcels, and an approach path overhead.",
    airspaceSummary:
      "The northeast sits directly beneath the Calgary International (CYYC) Class C control zone and, in places, under active approach paths. Nothing here is flown without a NAV CANADA authorisation in hand. An Advanced RPAS certificate is the minimum entry requirement.",
    adjacentRegions: ["calgary-centre", "calgary-nw", "calgary-se", "chestermere-area"],
  },
  {
    slug: "calgary-sw",
    name: "Southwest Calgary",
    shortName: "Calgary SW",
    description:
      "Calgary's southwest — Aspen Woods, Springbank Hill, Signal Hill, West Springs, Currie, Altadore, and south to Evergreen and Bridlewood. The city's densest concentration of high-value residential, where the mountain sightline is a priced feature. Shooting it means proving the sightline exists, which is a west-facing, late-day problem.",
    airspaceSummary:
      "Southwest Calgary sits between the CYYC control zone and Springbank (CYBW) to the west. Most of the quadrant is controlled airspace requiring authorisation, and the CYBW boundary runs closer to the western communities than most operators expect.",
    adjacentRegions: ["calgary-centre", "calgary-se", "springbank-elbow"],
  },
  {
    slug: "calgary-se",
    name: "Southeast Calgary",
    shortName: "Calgary SE",
    description:
      "Calgary's southeast — Mahogany, Auburn Bay, Cranston, Seton, McKenzie Towne, Copperfield and the Deerfoot communities. Lake communities dominate: Mahogany and Auburn Bay both sell on water access, and water is the one subject that photographs completely differently from 90 metres than it does from the shore.",
    airspaceSummary:
      "The southeast sits under the southern edge of the CYYC control zone, with the constraint easing toward the city limit. Authorisation requirements change across the quadrant, so the boundary is checked per site rather than per suburb.",
    adjacentRegions: ["calgary-centre", "calgary-sw", "calgary-ne", "chestermere-area", "foothills-south"],
  },
  {
    slug: "springbank-elbow",
    name: "Springbank & Elbow Valley",
    shortName: "Springbank",
    description:
      "Springbank's acreage country and the Elbow Valley corridor — estate homes on five to twenty acres, golf-course enclaves, and long private approaches off Highway 8. Acreage is the hardest residential subject to sell from the ground and the easiest from the air: only altitude shows where the property line actually runs.",
    airspaceSummary:
      "This corridor sits inside or immediately adjacent to the Springbank Airport (CYBW) Class C control zone. CYBW is a busy training field, so authorisation here is routine work rather than an exception, and it is filed before every shoot.",
    adjacentRegions: ["calgary-sw", "calgary-nw", "rocky-view", "cochrane-foothills"],
  },
  {
    slug: "rocky-view",
    name: "Rocky View — Bearspaw & North",
    shortName: "Rocky View",
    description:
      "Rocky View County's acreage estates north and west of Calgary — Bearspaw, Watermark, Silverhorn, Church Ranches. Rolling ground, long gravel approaches, and properties where the house is a small part of what is being sold. The land is the asset, and the land needs a plan view.",
    airspaceSummary:
      "Rocky View straddles the boundary between the CYBW control zone and uncontrolled Class G airspace. Which side a given acreage falls on is not obvious from a map at driveway scale, so it is confirmed against NAV Drone per property.",
    adjacentRegions: ["calgary-nw", "springbank-elbow", "cochrane-foothills"],
  },
  {
    slug: "cochrane-foothills",
    name: "Cochrane & the Foothills",
    shortName: "Cochrane",
    description:
      "Cochrane and the foothills hamlets — Bragg Creek, Redwood Meadows, Water Valley, and the range roads between them. Cochrane is built on a hillside above the Bow, which means a listing here has vertical relief most Alberta towns do not, and a west-facing town gets its best light in the last hour of the day.",
    airspaceSummary:
      "Cochrane sits west of the Springbank (CYBW) control zone in largely uncontrolled Class G airspace, though the eastern approach to town runs closer to the CYBW boundary than the town limits suggest.",
    adjacentRegions: ["rocky-view", "springbank-elbow", "bow-valley", "cochrane-west"],
  },
  {
    slug: "cochrane-west",
    name: "West of Cochrane",
    shortName: "Cochrane West",
    description:
      "The country west and north of Cochrane — Ghost Lake, Cremona, Water Valley, Madden, Dog Pound and the range roads between. Open Class G country where the constraint stops being airspace and starts being wind. This is where the foothills begin to work on an airframe.",
    airspaceSummary:
      "Uncontrolled Class G airspace across the corridor, well clear of the CYBW control zone. The operational limit here is weather and daylight, not authorisation.",
    adjacentRegions: ["cochrane-foothills", "bow-valley", "qe2-corridor"],
  },
  {
    slug: "bow-valley",
    name: "Bow Valley & Canmore",
    shortName: "Bow Valley",
    description:
      "The Bow Valley corridor — Canmore's mountain neighbourhoods, Exshaw, Dead Man's Flats, Harvie Heights and Lac des Arcs. The most visually rewarding and most operationally difficult ground in the service area. Mountain scale flatters a wide shot and punishes a careless one: the valley funnels wind, the peaks eat the light early, and the park boundary is closer than most clients realise.",
    airspaceSummary:
      "The valley is largely uncontrolled Class G airspace, but the Canmore municipal heliport and the Banff National Park boundary both impose hard constraints, and the park boundary sits within a few kilometres of several Canmore neighbourhoods. Every Bow Valley flight is planned against the park line first.",
    adjacentRegions: ["cochrane-foothills", "banff-area", "cochrane-west"],
  },
  {
    slug: "banff-area",
    name: "Banff & the National Park",
    shortName: "Banff",
    description:
      "Banff townsite, Tunnel Mountain, Middle Springs, Lake Louise and Castle Junction. The most photographed landscape in Canada and, for a drone operator, effectively closed. These pages exist to answer the question honestly rather than to sell a shoot that cannot legally happen.",
    airspaceSummary:
      "Banff National Park. Parks Canada prohibits launching, landing and operating a drone within the park without a permit issued for a restricted purpose. Commercial listing and marketing work is not a purpose those permits are ordinarily granted for.",
    adjacentRegions: ["bow-valley"],
  },
  {
    slug: "chestermere-area",
    name: "Chestermere",
    shortName: "Chestermere",
    description:
      "Alberta's lake city — Westmere, Rainbow Falls, Kinniburgh, Chelsea, The Cove and the lakeshore. A town organised around a single body of water, which makes the aerial case obvious: from the ground a lakefront home shows a fence and a dock, and from 90 metres it shows the reason someone paid for it.",
    airspaceSummary:
      "Chestermere sits immediately east of Calgary under the outer edge of the CYYC control zone. The boundary runs through the area rather than around it, so authorisation status is confirmed per address, not per town.",
    adjacentRegions: ["calgary-ne", "calgary-se", "rocky-view-east", "wheatland"],
  },
  {
    slug: "rocky-view-east",
    name: "Rocky View East",
    shortName: "Rocky View East",
    description:
      "The country east of Calgary — Langdon, Conrich, Indus, Dalemead, Delacour, Balzac. Industrial yards, logistics parcels, quarter sections and the fastest-changing land use in the region. Construction progress documentation lives here, and progress work is the one aerial product that compounds: the value is the sequence, not the single frame.",
    airspaceSummary:
      "A mixed corridor. The western edge falls under the CYYC control zone and the Balzac area sits close to the northern approach, while the eastern parcels are uncontrolled Class G. The line is checked per site.",
    adjacentRegions: ["calgary-ne", "chestermere-area", "wheatland", "airdrie-corridor"],
  },
  {
    slug: "airdrie-corridor",
    name: "Airdrie",
    shortName: "Airdrie",
    description:
      "Airdrie and its communities — Coopers Crossing, Bayside, Kings Heights, Windsong, Luxstone, Sagewood, Ravenswood. One of the fastest-growing cities in Canada, which in aerial terms means new-build subdivisions photographed at phase boundaries: the same frame in March and September is a marketing asset a developer cannot buy retroactively.",
    airspaceSummary:
      "Airdrie sits between its own aerodrome and the northern edge of the CYYC control zone. Both constraints apply across parts of the city, so authorisation status is established per address before a shoot is booked.",
    adjacentRegions: ["calgary-nw", "calgary-ne", "rocky-view", "rocky-view-east", "qe2-corridor"],
  },
  {
    slug: "qe2-corridor",
    name: "The QE2 Corridor",
    shortName: "QE2 Corridor",
    description:
      "The Highway 2 towns north of Calgary — Crossfield, Carstairs, Didsbury, Olds, Sundre. Agricultural land, small-town commercial, and the quarter-section geometry that only reads from directly overhead. A field boundary is invisible at eye level and obvious at 120 metres.",
    airspaceSummary:
      "Predominantly uncontrolled Class G airspace, with local aerodromes at several of the towns creating their own small constraints. The corridor is checked per town rather than treated as uniform.",
    adjacentRegions: ["airdrie-corridor", "cochrane-west", "rocky-view"],
  },
  {
    slug: "foothills-south",
    name: "Foothills & the South",
    shortName: "Foothills South",
    description:
      "South of Calgary — Okotoks, High River, Diamond Valley, Longview, Nanton, Millarville, Priddis, De Winton, Aldersyde, Cayley. Where the foothills roll into ranch country. Big parcels, big skies, and the province's cleanest west-facing golden hour, because there is nothing between the camera and the Rockies.",
    airspaceSummary:
      "Largely uncontrolled Class G airspace, with the Okotoks Air Park and the High River aerodrome creating localised constraints. The southern parcels are as unconstrained as anywhere in the service area.",
    adjacentRegions: ["calgary-se", "calgary-sw", "springbank-elbow", "wheatland"],
  },
  {
    slug: "wheatland",
    name: "Wheatland County",
    shortName: "Wheatland",
    description:
      "East to Strathmore, Standard, Rosebud and Hussar. Prairie proper: section roads on a grid, grain infrastructure, and light that runs uninterrupted from horizon to horizon. The flattest ground in the service area and, for agricultural and industrial subjects, the most legible from the air.",
    airspaceSummary:
      "Uncontrolled Class G airspace across the county, clear of the Calgary control zone. Constraints here are weather and daylight rather than authorisation.",
    adjacentRegions: ["chestermere-area", "rocky-view-east", "foothills-south"],
  },
];

export function getRegion(slug: string): Region | undefined {
  return REGIONS.find((r) => r.slug === slug);
}
